import type { CategoryContent } from '../row.js';

/**
 * Macro-economie, Micro-economie - afgesplitst van de oude categorie
 * "Economics & Financial History" (markets.ts's ECONOMICS/MORE_ECONOMICS,
 * nu uitgefaseerd). Finance & Structured Products (FINANCE/MORE_FINANCE in
 * markets.ts) is een andere categorie en blijft ongewijzigd.
 */

export const MACROECON: CategoryContent = {
  bscba: [
    [
      'Wat meet BBP?',
      [
        'De totale geldwaarde van alle eindgoederen en diensten geproduceerd binnen een land in een periode',
        'Het totale inkomen verdiend door alle burgers van een land, ongeacht waar het werd verdiend',
        'De totale waarde van uitvoer minus invoer in internationale handel',
        'De totale rijkdom gehouden door alle mensen en bedrijven in een land',
      ],
      0,
      'BBP meet stroom van eindproductie binnen een grondgebied, niet rijkdom of inkomen van burgers in het buitenland.',
    ],
    [
      'Wat is het verschil tussen reëel en nominaal BBP?',
      [
        'Reëel BBP corrigeert voor prijsveranderingen; nominaal BBP is gemeten tegen huidige prijzen',
        'Reëel BBP sluit overheidsuitgaven uit en nominaal BBP omvat ze',
        'Reëel BBP meet alleen verhandelde goederen en nominaal BBP omvat diensten',
        'Reëel BBP is altijd kleiner dan nominaal BBP',
      ],
      0,
      'Reëel BBP corrigeert voor inflatie zodat vergelijkingen echte productiewijzigingen tonen; nominaal weerspiegelt huidige prijzen.',
    ],
    [
      'Wat is inflatie?',
      [
        'Een aanhoudende stijging van het algemene prijsniveau van goederen en diensten in de tijd',
        'Een toename van de hoeveelheid geld in omloop in een economie',
        'Een situatie waar sommige prijzen stijgen terwijl anderen dalen',
        'De snelheid waarmee de centrale bank jaarlijks de geldhoeveelheid verhoogt',
      ],
      0,
      'Inflatie is de snelheid waarop het gemiddelde prijsniveau stijgt; het ondermijnt koopkracht.',
    ],
    [
      'Wat zijn de voornaamste soorten werkloosheid?',
      [
        'Wrijvings-, structurele en conjuncturele',
        'Tijdelijke, permanente en onvrijwillige',
        'Klassieke, Keynesiaanse en aanbodgebaseerde',
        'Zichtbare, verborgen en overgangswerkloosheid',
      ],
      0,
      'Wrijving ontstaat uit werkzoeken, structureel uit vaardigheidsmismatch, conjunctureel uit zwakke vraag in recessies.',
    ],
    [
      'Wat beschrijft de Phillips-curve?',
      [
        'Een negatieve relatie tussen inflatie- en werkloosheidsvoeten',
        'Een positieve relatie tussen lonen en arbeidsproductiviteit',
        'Een negatieve relatie tussen rentevoeten en geldhoeveelheid',
        'Een positieve relatie tussen overheidsuitgaven en particuliere investeringen',
      ],
      0,
      'De Phillips-curve toont dat lagere werkloosheid samenhangt met hogere inflatie, hoewel de relatie in de tijd is verschoven.',
    ],
    [
      'Wat is het verschil tussen geldhoeveelheid en monetaire basis?',
      [
        'De geldhoeveelheid omvat contanten en deposito\'s; de monetaire basis is enkel munt en reserves',
        'De monetaire basis is groter dan de geldhoeveelheid',
        'Banken controleren de geldhoeveelheid; centrale banken de monetaire basis',
        'De geldhoeveelheid is een voorraad; de monetaire basis is een stroom',
      ],
      0,
      'De monetaire basis (contanten en reserves) is het meest liquide; de geldhoeveelheid omvat contanten, betaalrekeningen en meer.',
    ],
    [
      'Wat is expansionair begrotingsbeleid?',
      [
        'Overheid verhoogt uitgaven of verlaagt belastingen om economie in recessie aan te porren',
        'Overheid draait begrotingstekort in plaats van overschot',
        'Overheid investeert in infrastructuur en onderwijs',
        'Centrale bank verhoogt geldhoeveelheid om rentevoeten te verlagen',
      ],
      0,
      'Expansionair begrotingsbeleid verhoogt vraag wanneer economie zwak is door hogere uitgaven of lagere belastingen.',
    ],
    [
      'Wat is het vermenigvuldigingseffect?',
      [
        'Een initiële uitgavenstijging leidt tot grotere totale outputstijging terwijl geld door economie circuleert',
        'Het effect van verdubbeling van geldhoeveelheid op prijsniveau',
        'De winst die een bedrijf op elke dollar omzet maakt',
        'De efficiëntietoename wanneer een bedrijf verdubbeld in grootte',
      ],
      0,
      'Het vermenigvuldiger toont hoe initiële uitgaven door economie uitbreiden terwijl ontvangers hun inkomen uitgeven.',
    ],
    [
      'Wat is crowding out (verdringing)?',
      [
        'Overheidsschuld tilt rentevoeten, waardoor particuliere investeringen afnemen',
        'Grote bedrijven voorkomen dat kleinere bedrijven markt betreden',
        'Situatie waarbij openbare goederen particuliere productie verhinderen',
        'Inflatie vermindert reële waarde van besparingen van mensen',
      ],
      0,
      'Verdringing vindt plaats wanneer overheidsschuld rentevoeten opdrijft, wat particuliere leningen en investeringen duurder maakt.',
    ],
    [
      'Wat is de productiemogelijkhedencurve?',
      [
        'Curve toont maximale combinaties van twee goederen die economie kan produceren met beschikbare middelen',
        'Grafiek toont hoe productiekosten variëren met volume',
        'Grens tussen landen die handelsstromen bepaalt',
        'Lijn toont relatie tussen arbeidsproductiviteit en reële lonen',
      ],
      0,
      'PPF toont afruiling tussen productie van verschillende goederen; punten eronder vertegenwoordigen inefficiëntie.',
    ],
    [
      'Wat toont de aggregate-vraagcurve?',
      [
        'De totale hoeveelheid goederen en diensten gevraagd tegen elk prijsniveau',
        'De hoeveelheid particuliere investeringen gevraagd tegen elk renteniveau',
        'De hoeveelheid van één specifiek goed gevraagd tegen verschillende prijzen',
        'De hoeveelheid overheidsuitgaven gepland tegen elk inkomstniveau',
      ],
      0,
      'Aggregatevraag is totale uitgaven aan eindgoederen tegen elk overall prijsniveau; het helt naar beneden af.',
    ],
    [
      'Wat is stagflatie?',
      [
        'Gelijktijdige combinatie van hoge inflatie en hoge werkloosheid of lage groei',
        'Tijdelijk pauze in inflatie gevolgd door hervatting van prijsstijging',
        'Situatie waarin werkloosheid daalt terwijl inflatie stijgt',
        'Periode van aanhoudende begrotingstekorten zonder inflatie',
      ],
      0,
      'Stagflatie combineert stagnatie met inflatie, wat beleidsmakers uitdaagt en de jaren zeventig plagde.',
    ],
    [
      'Wat is comparatief voordeel in internationale handel?',
      [
        'Vermogen iets tegen lagere opportuniteitskosten dan ander land te produceren',
        'Vermogen meer van iets te produceren dan ander land met dezelfde middelen',
        'Voordeel dat rijke landen over arme landen hebben',
        'Tariefsbesherming die land aan binnenlandse industrie verleent',
      ],
      0,
      'Comparatief voordeel bepaalt specialisatie: landen moeten specialiseren in wat ze relatief goedkoper kunnen produceren.',
    ],
    [
      'Wat is koopkrachtpariteit?',
      [
        'Hetzelfde mandje goederen zou dezelfde prijs hebben in verschillende landen tegen huidige wisselkoersen',
        'Vermogen van munt om waarde stabiel te houden in de tijd',
        'Overheidsbeleid om prijzen van essentiële goederen stabiel te houden',
        'Overeenkomst tussen landen over vaste wisselkoers',
      ],
      0,
      'KKP suggereert wisselkoersen aanpassen zodat identieke goederen internationaal dezelfde kosten; afwijkingen ontstaan door handelskosten.',
    ],
    [
      'Wat is de functie van centrale bank?',
      [
        'Geldhoeveelheidcontrole, monetair beleid bepalen en lender of last resort',
        'Zorgen dat overheidsbegrotingen sluitend zijn en fiscale uitgaven goedkeuren',
        'Prijzen vaststellen voor alle goederen ter voorkoming van inflatie',
        'Commerciële bankservices verlenen aan het publiek',
      ],
      0,
      'Centrale banken controleren monetair beleid via rentes en geldhoeveelheid; stabiliseren financiële systemen.',
    ],
  ],
  msc: [
    [
      'Wat stelt de Lucas-kritiek over beleidsevaluatie?',
      [
        'Geschatte relaties verschuiven zodra het beleid verandert, omdat de beslisregels van actoren afhangen van het beleidsregime',
        'Rationale verwachtingen maken systematisch monetair beleid volledig ineffectief op elke horizon, hoe kort ook',
        'Econometrische modellen zijn niet identificeerbaar zonder exclusierestricties',
        'Vraagsturing wordt ondermijnd door verdringing van particuliere investeringen',
      ],
      0,
      'Gepubliceerd in 1976. Het is het argument dat de macro-economie richting expliciet microgefundeerde modellen met diepe parameters duwde.',
    ],
    [
      'Wat bepaalt in het Solow-model de lange-termijngroei van output per werknemer?',
      [
        'De snelheid van technologische vooruitgang',
        'De spaarquote',
        'De bevolkingsgroei',
        'De afschrijvingsvoet van kapitaal',
      ],
      0,
      'De spaarquote bepaalt het niveau van het evenwichtsgroeipad, niet de helling ervan - en precies dat gat is wat de endogene-groeitheorie wilde opvullen.',
    ],
    [
      'Het Mundell-Fleming-trilemma stelt dat een land ten hoogste twee van welke drie kan kiezen?',
      [
        'Een vaste wisselkoers, vrij kapitaalverkeer en onafhankelijk monetair beleid',
        'Volledige werkgelegenheid, prijsstabiliteit en extern evenwicht',
        'Begrotingshoudbaarheid, financiële stabiliteit en lage, stabiele consumptie-inflatie',
        'Een muntunie, een bankenunie en een fiscale unie',
      ],
      0,
      'Het is het kader waarin de discussies over het ontwerp van de eurozone werden gevoerd, en de reden dat kapitaalcontroles weer een serieus onderwerp van gesprek werden.',
    ],
    [
      'Wat is de "natuurlijke werkloosheid" in de zin van Friedman en Phelps?',
      [
        'De voet die verenigbaar is met stabiele inflatie zodra verwachtingen zich hebben aangepast',
        'De voet waarbij het aantal vacatures precies gelijk is aan het aantal werklozen',
        'De minimumvoet die haalbaar is zonder kapitaalverdieping',
        'De voet die overeenkomt met nul onvrijwillige werkloosheid',
      ],
      0,
      'De met verwachtingen uitgebreide Phillips-curve is wat de lange-termijnruil laat verdwijnen, en NAIRU is dezelfde gedachte onder een andere naam.',
    ],
    [
      'Wat voorspelt het Balassa-Samuelson-effect?',
      [
        'Landen met snellere productiviteitsgroei in de verhandelbare sector ervaren reële appreciatie van de wisselkoers',
        'Koopkrachtpariteit geldt precies op de lange termijn, zodat reële wisselkoersen terugkeren naar een constant niveau',
        'Nominale wisselkoersen convergeren naar renteverschillen',
        'Prijsniveaus zijn gelijk tussen landen bij marktwisselkoersen',
      ],
      0,
      'Prijzen van niet-verhandelbare goederen stijgen met lonen die in de verhandelbare sector worden vastgesteld, en daarom lijken rijke landen duur tegen marktwisselkoersen.',
    ],
    [
      'Wat is de "Lucas-paradox"?',
      [
        'Kapitaal stroomt niet van rijke naar arme landen in de mate die rendementsverschillen op kapitaal zouden voorspellen',
        'Consumptie is gladder dan inkomen over de conjunctuurcyclus',
        'Geld is neutraal op de lange termijn maar niet op de korte termijn',
        'Verwacht beleid heeft geen effect op reële output, zodat alleen de onverwachte component van een beleidswijziging telt',
      ],
      0,
      'Institutionele kwaliteit en menselijk kapitaal zijn de gangbare verklaringen, en het blijft een raadsel aan de marge.',
    ],
    [
      'Wat is het verschil tussen bbp en bni?',
      [
        'Bbp meet de output geproduceerd binnen een gebied; bni meet het inkomen dat toevalt aan de ingezetenen ervan',
        'Bbp omvat overheidsuitgaven en bni niet',
        'Bbp wordt gemeten in marktprijzen en bni tegen factorkosten',
        'Bbp is nominaal en bni is altijd reëel, zodat de twee niet vergelijkbaar zijn zonder een deflatorreeks',
      ],
      0,
      'Het verschil doet er enorm toe voor economieën met een grote in buitenlandse handen zijnde kapitaalvoorraad of grote overmakingsstromen - Ierland is het standaardvoorbeeld.',
    ],
    [
      'In het overlappende-generaties-model van Diamond (1965), welke voorwaarde signaleert dynamische inefficiëntie in het competitieve evenwicht?',
      [
        'De kapitaalvoorraad overstijgt het gouden-regel-niveau, zodat de evenwichtsrente onder de bevolkingsgroei ligt',
        'De spaarquote van de economie overstijgt wat een welwillende planner die een volledig dynamisch optimalisatieprobleem over een oneindige horizon oplost, zelf onafhankelijk zou kiezen',
        'Het marginale product van kapitaal daalt onder alleen de snelheid van technologische vooruitgang',
        'Jonge actoren sparen meer dan oude actoren ontsparen in elke periode, wat marktclearing doorbreekt',
      ],
      0,
      'Een OLG-economie kan kapitaal oveaccumuleren: als de evenwichtsrente onder bevolkingsgroei ligt, heeft de economie meer kapitaal dan nodig. Diamond (1965) gaf het klassieke argument waarom een pensioenstelsel welvaartsverhogend kan zijn.',
    ],
    [
      'De oorspronkelijke Taylor-regel (1993) stelt de beleidsrente vast als een functie van wat, met welk kenmerk?',
      [
        'Afwijkingen van inflatie ten opzichte van de doelstelling en van output ten opzichte van potentieel, waarbij de inflatiecoëfficiënt groter is dan één',
        'De groeivoet van een beoogde monetaire aggregaat, elke periode aangepast voor verschuivingen in de omloopsnelheid ten opzichte van een vaste doelbandbreedte',
        'De nominale wisselkoers ten opzichte van een mandje van handelspartnervaluta\'s',
        'Alleen de werkloosheid, gewogen naar de afwijking ten opzichte van de natuurlijke voet',
      ],
      0,
      'Taylors regel verhoogt rente meer dan één-op-één bij inflatiestijging - het "Taylor-principe". John Taylor introduceerde het in 1993 als Fed-beschrijving. Een coëfficiënt onder één kan indeterminacy en zelfvervullende verwachtingen opleveren.',
    ],
    [
      'De wet van Okun, in haar standaard "gap"-vorm, verbindt welke twee variabelen, en welke ruwe verhouding is historisch gebruikelijk in de VS?',
      [
        'De outputgap met de afwijking van werkloosheid ten opzichte van de natuurlijke voet, in ruwweg een verhouding van 2:1',
        'De inflatie met de afwijking van werkloosheid ten opzichte van NAIRU onder de met verwachtingen uitgebreide Phillips-curve, één op één',
        'De verandering in arbeidsparticipatie met de verandering in reële lonen over de conjunctuurcyclus',
        'De groeivoet van werkgelegenheid met de groeivoet van alleen de kapitaalvoorraad',
      ],
      0,
      'Okun (1962): één procentpunt werkloosheidskloof dicht = twee procentpunt outputgroei. Statistische regelmaat, niet structureel. Is sinds 2008 verzwakt. Snelle vuistregel voor outputgap uit werkloosheid.',
    ],
    [
      'In de reële-conjunctuurcyclustheorie (Kydland en Prescott, 1982), wat wordt geïdentificeerd als de primaire aanjager van aggregate schommelingen?',
      [
        'Exogene productiviteitsschokken, doorgegeven via de optimale intertemporele substitutie van arbeid en consumptie door huishoudens',
        'Rigide nominale lonen en prijzen die verhinderen dat arbeids- en goederenmarkten snel clearen na vraagschokken',
        'Zelfvervullende verschuivingen in verwachtingen over toekomstige aggregate vraag, gecoördineerd tussen bedrijven en consumenten',
        'Exogene verschuivingen in de geldhoeveelheid die werken via kortetermijn nominale prijs- en loonrigiditeiten',
      ],
      0,
      'RBC-theorie: conjunctuurcycli zijn efficiënte evenwichtsreactie op technologieschokken. Kydland-Prescott (1982) introduceerden calibratie; Nobelprijs 2004. De claim dat recessies efficiënt zijn riep Nieuw-Keynesiaanse tegenbeweging op.',
    ],
    [
      'In het Kydland-Prescott/Barro-Gordon-model van monetair beleid, waarom genereert discretionair beleid een inflatiebias vergeleken met een geloofwaardige nul-inflatieregel?',
      [
        'De verleiding om een kortetermijn-Phillips-curve-verrassing uit te buiten maakt een nul-inflatiebelofte ongeloofwaardig, zodat rationele actoren dit inprijzen',
        'Centrale banken onder discretie geven systematisch de voorkeur aan hogere inflatie omdat het de reële waarde van de uitstaande nominale staatsschuld uitholt',
        'Discretie staat mechanisch toe dat de geldhoeveelheid elke periode sneller groeit dan onder een geloofwaardige vaste monetaire regel',
        'Onder discretie eisen loononderhandelaars bij elke opeenvolgende contractherziening een aanmerkelijk grotere inflatierisicopremie',
      ],
      0,
      'Discretionaire beleidsmakers hebben prikkel voor verrassingsinflatie; rationele actoren anticiperen en prijzen dit in. Resultaat: hogere inflatie zonder outputwinst. Kydland-Prescott (1977) en Barro-Gordon (1983) rechtvaardigen centrale-bankonafhankelijkheid.',
    ],
    [
      'De "tweelingtekort"-hypothese verbindt een begrotingstekort aan een lopende-rekeningtekort via welke nationale-rekeningenidentiteit?',
      [
        'Het lopende-rekeningsaldo is gelijk aan private besparingen minus binnenlandse investeringen minus het begrotingstekort van de overheid',
        'Een handelsoverschot moet ontstaan omdat overheidsschuld volledig wordt gefinancierd door buitenlandse officiële reserves',
        'Een kapitaalrekeningsoverschot is vereist omdat binnenlandse besparingen altijd gelijk moeten zijn aan binnenlandse investeringen',
        'Een begrotingsoverschot volgend jaar is vereist door de budgetrestrictie van de overheid',
      ],
      0,
      'Lopende-rekeningsaldo = private besparingen - investeringen - begrotingstekort. Groter tekort verhoogt lopende-rekeningtekort. Genoemd naar jaren tachtig onder Reagan. Ricardiaanse equivalentie suggereert compensatie door huishoudenbesparingen.',
    ],
    [
      'In standaard groeiboekhouding wordt het "Solow-residu" berekend als wat?',
      [
        'Outputgroei minus de aandeel-gewogen groei van kapitaal- en arbeidsinputs, toegeschreven aan totale factorproductiviteit',
        'Het gat tussen feitelijk reëel bbp en een econometrisch geschatte maatstaf van potentieel bbp binnen een gegeven kalenderkwartaal',
        'De onverklaarde variantie die overblijft na regressie van kwartaalgroei van output op vertraagde monetaire aggregaten en rentes',
        'Het verschil tussen bruto- en netto-investeringen, gecorrigeerd voor een veronderstelde afschrijvingsvoet van kapitaal in de tijd',
      ],
      0,
      'Outputgroei = kapitaalgroei × aandeel + arbeid × aandeel + restterm (Solow-residu = TFP). Solow (1957). Abramovitz: "maatstaf van onze onwetendheid". Centraal in ontwikkelingsdiscussies.',
    ],
    [
      'Mundells criteria voor de optimale muntzone (1961) identificeren wat als het belangrijkste alternatief voor onafhankelijke wisselkoersaanpassing bij het opvangen van een asymmetrische schok tussen regio\'s die één munt delen?',
      [
        'Voldoende arbeidsmobiliteit tussen de regio\'s van de muntunie, als vervanging voor de wisselkoersaanpassing die ze niet langer hebben',
        'Een gemeenschappelijke banktoezichthouder met geharmoniseerde kapitaaleisen',
        'Gesynchroniseerde nominale loonvormingsinstituties in alle lidstaten',
        'Een gedeeld extern tarief dat uniform wordt toegepast op handel met niet-leden',
      ],
      0,
      'Zonder munteigen devaluatie is factormobiliteit het alternatief. Mundell (1961) stelde dit als maatstaf voor muntunies. Blanchard-Katz: arbeidsmobiliteit over Europa veel lager dan VS - zorg voor eurostabiliteit.',
    ],
  ],
  phd: [
    [
      'Wat verklaart bankruns volgens het Diamond-Dybvig-model?',
      [
        'Een coördinatiefalen met meerdere evenwichten, ontstaan door opeisbare deposito\'s met een volgtijdelijke bedieningsregel die illiquide activa financieren',
        'Een rationele reactie op werkelijke insolventie, zodra deze aan spaarders is onthuld door een openbaar signaal over de kwaliteit van activa',
        'Een gevolg van looptijdmismatch onder complete markten',
        'Het resultaat van depositoverzekering die de prikkels van spaarders vertekent',
      ],
      0,
      'Het run-evenwicht bestaat zelfs wanneer de bank solvabel is, en dat is het argument voor depositoverzekering en voor een lender of last resort.',
    ],
    [
      'Wat modelleert het Diamond-Mortensen-Pissarides-raamwerk?',
      [
        'Arbeidsmarkten als een zoek- en matchingproces met een matchingfunctie en Nash-onderhandelde lonen',
        'Loonrigiditeit die ontstaat uit impliciete langetermijncontracten tussen bedrijven en hun zittende personeel',
        'Werkloosheid als de uitkomst van efficiëntielonen die worden betaald om lijntrekken te ontmoedigen',
        'Banencreatie gedreven door toetreding van bedrijven onder monopolistische concurrentie',
      ],
      0,
      'Het gaf de macro-economie een hanteerbare theorie van werkloosheid als een evenwichtsstroomverschijnsel in plaats van een disequilibriumrestterm. Nobelprijs, 2010.',
    ],
    [
      'Wat is de belangrijkste moderne kritiek op het gebruik van het Hodrick-Prescott-filter op macro-tijdreeksen?',
      [
        'Het genereert kunstmatige dynamiek en lijdt aan zware vertekening aan het einde van de steekproef',
        'Het kan niet worden toegepast op reeksen met een eenheidswortel',
        'Het vereist dat de cyclus strikt periodiek is',
        'Het onderschat systematisch de amplitude van recessies ten opzichte van expansies',
      ],
      0,
      'Hamiltons "Why You Should Never Use the HP Filter" (2018) is de standaardreferentie; het eindpuntprobleem is waarom real-time outputgaps zo zwaar worden herzien.',
    ],
    [
      'Wat gebeurt er bij de nulgrens van de rente in een standaard Nieuw-Keynesiaans model?',
      [
        'De beleidsrente kan niet dalen tot het niveau dat het model vereist, en fiscale multipliers stijgen',
        'Inflatieverwachtingen worden perfect verankerd door de aangekondigde doelstelling',
        'De Phillips-curve wordt verticaal en monetair beleid herwint grip',
        'Ricardiaanse equivalentie faalt, wat belastingverlagingen het enige overgebleven effectieve beleidsinstrument maakt',
      ],
      0,
      'Dezelfde modellen impliceren een "forward guidance-puzzel": aangekondigde toekomstige rentepaden hebben onwaarschijnlijk grote effecten, wat een van de redenen is waarom de literatuur naar heterogene-actor-varianten opschoof.',
    ],
    [
      'Wat documenteerden Reinhart en Rogoff over acht eeuwen data?',
      [
        'Terugkerende empirische regelmatigheden in staatsschuldwanbetaling, bankencrises en inflatiecrises',
        'Een stabiele omgekeerde relatie tussen inflatie en werkloosheid over regimes heen',
        'Dat financiële liberalisering de langetermijngroei betrouwbaar verhoogt',
        'Dat muntunies de frequentie van staatsschuldwanbetaling systematisch verminderen',
      ],
      0,
      'De invloed van het boek overtrof het bewijs op één punt: het bijbehorende 90%-schuldendrempelresultaat bleek later te berusten op een codeerfout en een betwiste weging.',
    ],
    [
      'Wat doet de Mortensen-Pissarides-matchingfunctie analytisch?',
      [
        'Vertaalt werkloosheid en vacatures naar aanwervingen',
        'Bepaalt het loon als functie van uitsluitend productiviteit',
        'Specificeert de stroom van werknemers tussen sectoren',
        'Leidt de Beveridge-curve af uit optimaliserend gedrag',
      ],
      0,
      'Het is met opzet een black box, en de Beveridge-curve is het empirische spoor ervan - de kritiek is dat het het mechanisme verhult dat het vertegenwoordigt.',
    ],
    [
      'Wat moet gelden voor "Ricardiaanse equivalentie"?',
      [
        'Oneindige horizons of perfect intergenerationeel altruïsme, lump-sum belastingen en geen liquiditeitsbeperkingen',
        'Sluitende begrotingen in elke periode, zodat intertemporele verschuiving van de belastingdruk helemaal niet mogelijk is',
        'Perfect voorzicht over toekomstige overheidsuitgaven alleen',
        'Constante marginale belastingtarieven in de tijd',
      ],
      0,
      'Barro, 1974. Elke schending van de voorwaarden is een kanaal waardoor tekorten er wél toe doen, en dat is precies wat de empirische literatuur najaagt.',
    ],
    [
      'Wat beschrijft de reeks regelmatigheden bekend als "Kaldor-feiten"?',
      [
        'Een breed stabiele kapitaal-outputratio, kapitaalaandeel en reële rente naast gestage groei van output per werknemer',
        'De neiging van industriële werkgelegenheid om te dalen met inkomen',
        'Convergentie van inkomensniveaus tussen open economieën, geconditioneerd op spaarquotes en accumulatie van menselijk kapitaal',
        'De relatie tussen ongelijkheid en groei over het ontwikkelingspad',
      ],
      0,
      'Ze zijn wat het evenwichtsgroeipad in het standaardmodel moest reproduceren - en de recente literatuur over factoraandelen is precies het debat of ze nog steeds gelden.',
    ],
    [
      'Wat voegt het Kiyotaki-Moore-model toe aan macro-financiële analyse?',
      [
        'Onderpandbeperkingen gekoppeld aan activaprijzen',
        'Een bankensector met looptijdmismatch en run-dynamiek',
        'Rigide prijzen in een monopolistisch competitieve goederensector',
        'Heterogene huishoudens die onverzekerbaar inkomensrisico lopen',
      ],
      0,
      'Kredietcycli, 1997. Het is de op onderpand gebaseerde tegenhanger van de op nettovermogen gebaseerde financiële versneller.',
    ],
    [
      'In de "sudden stop"-literatuur geassocieerd met Calvo, wat kenmerkt het verschijnsel in crises in opkomende markten?',
      [
        'Een abrupte, grote ommekeer in kapitaalinstroom die een scherpe aanpassing van de lopende rekening en reële depreciatie afdwingt',
        'Een geleidelijke, meerjarige daling van de ruilvoet van een land die traag en voorspelbaar zijn buitenlandse reserves uitholt',
        'De abrupte, vooraf aangekondigde overstap van een centrale bank van een vaste naar een vrij zwevende wisselkoers, getimed op een fiscale kalender',
        'Een eenmalige devaluatie die ver van tevoren wordt aangekondigd om verloren exportprijsconcurrentiekracht te herstellen',
      ],
      0,
      'Sudden stop: scherpe ommekeer in buitenlandse kapitaal die lopende-rekening snel sluit via depreciatie. Calvo, gemotiveerd door Mexicaanse/Aziatische/Argentijnse crises. Schokken uit marktbereidheid, niet fundamentals.',
    ],
    [
      'Sargent en Wallaces "unpleasant monetarist arithmetic" (1981) laat zien dat strakker geld vandaag de inflatie op lange termijn kan verhogen als wat geldt?',
      [
        'Begrotingstekorten worden niet aangepast, zodat langzamere geldgroei nu snellere geldgroei later vereist om aan de budgetrestrictie van de overheid te voldoen',
        'De centrale bank koppelt tegelijkertijd de nominale wisselkoers aan een geloofwaardige, laag-inflatoire ankermunt terwijl tekorten ongewijzigd doorlopen',
        'Loonovereenkomsten blijven geïndexeerd aan vertraagde inflatie over een horizon die aanmerkelijk langer is dan de eigen aangekondigde horizon van het desinflatiebeleid',
        'De omloopsnelheid van geld is sterk procyclisch en stijgt scherp en voorspelbaar tijdens elke desinflatie-episode',
      ],
      0,
      'Geldverkrapping vandaag dwingt toekomstige gemonetarisering als tekorten niet aanpassen. Sargent-Wallace (1981): fiscale houdbaarheid onlosmakelijk met inflatie - voorloper fiscale prijsniveautheorie.',
    ],
    [
      'De forward-premiumpuzzel in internationale financiën verwijst naar welke empirische regelmatigheid?',
      [
        'Valuta met hoge rente stijgen gemiddeld eerder in waarde dan dat ze dalen in de tijd, wat ongedekte renteparitéit tegenspreekt',
        'Termijnwisselkoersen zijn onvertekende voorspellers van toekomstige spotkoersen zodra risicopremies volledig zijn meegenomen',
        'Gedekte renteparitéit faalt persistent en met economisch grote marges, zelfs tussen G7-valutaparen onder normale marktomstandigheden',
        'Spotwisselkoersen zijn aanzienlijk volatieler dan verklaard kan worden door nieuws over renteverschillen alleen',
      ],
      0,
      'Ongedekte rentepariteit voorspelt depreciatie bij hoge rente; Fama (1984) vond appreciatie. Positieve "carry trade"-rendementen. Duurzame anomalie; variabele risicopremies of "peso-problemen" als verklaring.',
    ],
    [
      'In Calvo\'s gefaseerde-prijsvorming-model (1983), welke stochastische aanname genereert nominale prijsrigiditeit?',
      [
        'Elk bedrijf herziet zijn prijs met een vaste, exogene kans per periode, ongeacht de leeftijd van de prijs',
        'Bedrijven binden zich aan een vaste nominale prijs voor een deterministisch aantal periodes bepaald door een bedrijfstakbreed onderhandelde contractduur',
        'Bedrijven passen prijzen alleen aan wanneer de afwijking van hun frictieloze optimum een vaste menukostendrempel overschrijdt',
        'Bedrijven passen prijzen aan volgens een gefaseerd maar volledig voorspelbaar overlappend schema, zoals in het contractmodel van Taylor (1980)',
      ],
      0,
      'Calvo-model: elk bedrijf herziet prijs met constante kans. Optimale keuze hangt van aggregate toestand af. Levert voorwaartskijkende NK Phillips-curve op. Standaard in DSGE-modellen vanwege analytisch gemak.',
    ],
    [
      'De fiscale theorie van het prijsniveau stelt dat het prijsniveau wordt vastgepind door wat, in tegenstelling tot de standaard monetaristische visie?',
      [
        'De reële waarde van de schuld moet gelijk zijn aan de contante waarde van verwachte toekomstige primaire overschotten',
        'De groeivoet van de monetaire basis alleen, volledig onafhankelijk van welke fiscale variabele of schulddynamiek dan ook',
        'De nominale wisselkoerskoppeling eenzijdig gekozen door de fiscale autoriteit, zonder kapitaalcontroles of sterilisatie',
        'De omvang van de balans van de centrale bank strikt uitgedrukt als ratio tot nominaal bbp per kwartaal',
      ],
      0,
      'Fiscale prijsniveautheorie (Sims, Woodford, Cochrane): budgetrestrictie als waardering. "Actief" fiscaal beleid met vaste overschotten = prijsniveau verschuift om schuld aan te passen. Geeft fiscaal beleid inflatiekanaal.',
    ],
    [
      'Wat is het centrale conceptuele verschil tussen eerste-generatie (Krugman, 1979) en tweede-generatie (Obstfeld, 1994/96) modellen van muntcrises?',
      [
        'Eerste-generatie crises komen voort uit mechanisch uitgeputte reserves; tweede-generatie crises kunnen daarentegen zelfvervullend zijn',
        'Eerste-generatiemodellen beschrijven economieën die strikt onder vaste wisselkoersen opereren, terwijl tweede-generatiemodellen alleen volledig vrij zwevende wisselkoersregimes beschrijven',
        'Eerste-generatiemodellen zijn alleen van toepassing op ontwikkelde economieën, en tweede-generatiemodellen alleen op opkomende economieën',
        'Eerste-generatiecrises worden vooral gedreven door insolventie van de bankensector, en tweede-generatiecrises alleen door lopende-rekeningtekorten',
      ],
      0,
      'Krugman (1979): reserves uitputten door tekorten - voorzienbare crisis. Obstfeld: beleidsmaker weegt kosten/baten af - meerdere evenwichten. Koppeling kan instorten door verwachting alleen. Cruciaal voor ERM-crisis (1992).',
    ],
  ],
  professor: [
    [
      'Wat begrenst de Hansen-Jagannathan-grens?',
      [
        'De minimale volatiliteit van elke stochastische discontofactor die verenigbaar is met waargenomen activarendementen',
        'De maximale Sharpe-ratio die haalbaar is voor elke gemiddelde-variantie-efficiënte portefeuille van de verhandelde activa',
        'De verzameling arbitragevrije prijzen voor een redundant effect',
        'De variantie van consumptiegroei geïmpliceerd door een representatieve actor',
      ],
      0,
      'Het maakt van de equity-premiumpuzzel een scherpe uitspraak: de stochastische discontofactor moet veel volatieler zijn dan plausibele consumptiegebaseerde modellen kunnen leveren.',
    ],
    [
      'Epstein-Zin-recursievevoorkeuren splitsen welke twee zaken die tijdadditief verwacht nut samenvoegt?',
      [
        'Risicoaversie en de elasticiteit van intertemporele substitutie',
        'Tijdsvoorkeur en de intertemporele elasticiteit van arbeidsaanbod',
        'Ambiguïteitsaversie en risicoaversie over bekende verdelingen',
        'De discontovoet en de overlevingskans van de actor',
      ],
      0,
      'Die scheiding is wat langetermijnrisicomodellen in staat stelt een substantiële equity-premie te genereren zonder een onplausibele aversie tegen consumptieschommelingen.',
    ],
    [
      'Wat voegt het Aiyagari-model toe aan het neoklassieke groeiraamwerk?',
      [
        'Onverzekerbaar idiosyncratisch inkomensrisico met leenbeperkingen, wat voorzorgssparen genereert',
        'Overlappende generaties, elk met een eindige planningshorizon, ter vervanging van de oneindig levende actor',
        'Zoekfricties in de markt voor kapitaalgoederen',
        'Nominale rigiditeiten die monetair beleid niet-neutraal maken',
      ],
      0,
      'Incomplete markten duwen de evenwichtsrente onder de tijdsvoorkeurvoet en produceren een niet-degeneratieve vermogensverdeling - de voorloper van de moderne heterogene-actor-literatuur.',
    ],
    [
      'De financiële versneller van Bernanke, Gertler en Gilchrist werkt via welk mechanisme?',
      [
        'Een externe-financieringspremie die daalt met het nettovermogen van de kredietnemer, zodat schokken aan balansen versterkt worden en zich voortplanten',
        'Een bindende onderpandbeperking die de toegestane leverage volledig onafhankelijk vaststelt van de heersende activaprijzen',
        'Bankruns veroorzaakt door een openbaar signaal over de kwaliteit van activa',
        'Een spreiding tussen deposito- en kredietrentes vastgesteld door imperfecte concurrentie',
      ],
      0,
      'Omdat nettovermogen procyclisch is, is de premie anticyclisch, en een aanvankelijk kleine schok houdt aan - de agency-kostenmicrofundering is het Townsend-probleem van kostbare-staatsverificatie.',
    ],
    [
      'Wat is de centrale claim van de HANK-literatuur over monetaire transmissie?',
      [
        'Indirecte algemeen-evenwichtseffecten op arbeidsinkomen domineren het directe intertemporele-substitutiekanaal',
        'Monetair beleid werkt bijna volledig via het wisselkoerskanaal',
        'Rentewijzigingen beïnvloeden aggregate output alleen via hun effect op woninginvesteringen en bouwactiviteit',
        'Heterogeniteit tussen huishoudens laat de aggregate transmissie in essentie onveranderd',
      ],
      0,
      'Met realistische aandelen hand-to-mouth-huishoudens is het representatieve-actorkanaal klein, wat de aandacht verschuift naar wie het inkomen ontvangt en de schuld aanhoudt.',
    ],
    [
      'Wat voegt Krugmans "nieuwe handelstheorie" toe aan comparatief voordeel?',
      [
        'Toenemende schaalvoordelen en voorkeur voor variëteit genereren intra-industriële handel tussen vergelijkbare landen',
        'Factorvoorraden bepalen handelspatronen sterker dan technologie',
        'Handel verhoogt altijd het reële loon voor alle factoren',
        'Transportkosten elimineren handelswinsten voor gedifferentieerde goederen',
      ],
      0,
      'Het verklaart wat Heckscher-Ohlin niet kan: het meeste handel vindt plaats tussen vergelijkbare rijke landen in vergelijkbare goederen.',
    ],
    [
      'Wat is het "Mian-Sufi"-verhaal over de Grote Recessie?',
      [
        'Huishoudensleverage en haar geografische spreiding dreven de diepte en persistentie van de vraaginstorting',
        'Kapitaaltekorten bij banken waren de bindende beperking op het herstel',
        'Monetair beleid was onvoldoende expansief bij de nulgrens',
        'Ineenstorting van de wereldhandel plantte de schok internationaal voort, wat de neergang synchroon maakte tussen anders niet-verbonden economieën',
      ],
      0,
      'Hun variatie op districtsniveau is wat het huishoudensbalanskanaal identificeerbaar maakte in plaats van louter plausibel.',
    ],
    [
      'Wat vereist de r > g-claim uit "Kapitaal in de 21e eeuw" werkelijk?',
      [
        'Een rendement op kapitaal dat persistent boven de groei ligt',
        'Dat het kapitaalaandeel wordt vastgelegd door technologie',
        'Dat kapitaalinkomen voor altijd onbelast blijft',
        'Dat erfenis de enige bron van vermogensaccumulatie is',
      ],
      0,
      'Of de aggregate substitutie-elasticiteit boven één ligt, is waar het technische argument zich concentreerde, en de vermogensdata zelf werd afzonderlijk betwist.',
    ],
    [
      'Waar komt de "geloofwaardigheidsrevolutie"-kritiek op structurele macro-economie op neer?',
      [
        'Identificatie moet komen uit transparante, verdedigbare variatie in plaats van uit opgelegde modelbeperkingen',
        'Structurele modellen kunnen niet geschat worden met beschikbare data',
        'Alleen gerandomiseerde experimenten leveren geldig causaal bewijs',
        'Macro-aggregaten zijn te ruizig voor enige causale inferentie, zodat alleen micro-econometrisch bewijs een beleidsclaim kan onderbouwen',
      ],
      0,
      'Het tegenargument is dat beleidscounterfactuals structuur nodig hebben, en dat is waarom het vakgebied convergeerde naar het gebruik van geloofwaardige microschattingen om structurele modellen te disciplineren.',
    ],
    [
      'Wat is de "safe asset shortage"-hypothese?',
      [
        'De vraag naar veilige waardeopslag overtreft het aanbod, wat risicovrije rentes drukt en risiconame elders opdrijft',
        'Het aanbod van staatsschuld wordt in alle grote economieën beperkt door begrotingsregels',
        'Banken houden onvoldoende kapitaal aan tegen veilige activa onder Basel III',
        'Veilige activa zijn verkeerd geprijsd door indexvolgende vraag, wat de meest breed gehouden uitgiften mechanisch opdrijft',
      ],
      0,
      'Caballero, Farhi en Gourinchas. Het verbindt de mondiale spaaroverschot, lage rentes en risicotransformatie in de financiële sector tot één verhaal.',
    ],
    [
      'Welk probleem in de theorie van muntcrises loste de global-games-benadering van Morris en Shin (1998) op?',
      [
        'Het voegde kleine hoeveelheden private, ruizige informatie toe aan het individuele signaal van elke actor, wat een unieke evenwichtsuitkomst selecteert uit de veelheid',
        'Het bewees dat een speculatieve aanval algemene kennis van het exacte reserveniveau van de centrale bank vereist om überhaupt plaats te vinden',
        'Het toonde dat zelfvervullende muntcrises volledig onmogelijk worden zodra termijnmarkten compleet en volledig frictieloos zijn',
        'Het demonstreerde dat tweede-generatie-crisismodellen volledig samenvallen met eerste-generatiemodellen zodra verwachtingen rationeel worden',
      ],
      0,
      'Tweede-generatiemodellen: meerdere evenwichten onder algemene kennis - geen duidelijkheid wanneer crisis optreedt. Morris-Shin voegden "global games"-techniek toe: privaat ruizige signalen breken algemene kennis en selecteren uniek evenwicht. Toepasbaar op bankruns en herfinancieringscrises.',
    ],
    [
      'In het Gertler-Kiyotaki-raamwerk van financiële intermediatie, wat beperkt banken\'s vermogen om krediet te verstrekken tijdens een crisis?',
      [
        'Een moral-hazard-beperking die de leverage van banken koppelt aan het nettovermogen van de intermediair, zodat schokken aan bankkapitaal de kredietverstrekking direct beperken',
        'Een wettelijke reserveverplichting vastgesteld door de banktoezichthouder als een vaste multiple van verzekerde retaildeposito\'s, jaarlijks herzien bij wet',
        'Een vast loan-to-value-plafond dat uniform door regelgeving wordt toegepast op elke categorie kredietnemer en elke bank',
        'De nulgrens op de rente die de centrale bank betaalt op reservetegoeden van banken',
      ],
      0,
      'Gertler-Kiyotaki: banken onderhevig aan agency-probleem - spaarders lenen tot multiple van bankiersvermogen. Schokken aan bankkapitaal beperken creditaanbod. Rechtvaardigt centrale bank als laatste-redmiddel intermediair met balans.',
    ],
    [
      'Jordà\'s local-projection-methode (2005) voor het schatten van impulsresponsies verschilt van een VAR vooral door wat te doen?',
      [
        'Een afzonderlijke directe regressie van de uitkomst op elke horizon op de schok uitvoeren, in plaats van één systeem vooruit te itereren',
        'Elke geschatte impulsrespons dwingen strikt monotoon naar nul te vervallen, puur door wiskundige constructie',
        'Vereisen dat de schokreeks volledig Cholesky-orthogonaal wordt gemaakt voordat de respons op één horizon geschat kan worden, ongeacht steekproefgrootte',
        'Het gehele dynamische systeem schatten in continue tijd in plaats van op de door de onderzoeker gekozen discrete datafrequentie',
      ],
      0,
      'VAR itereert één geschat systeem; local projections voeren afzonderlijke regressies per horizon uit. VAR: misspecificatie stapeelt; LP: robuuster, laat niet-lineaire responsies toe. LP populair in fiscale multiplier- en transmissiestudies.',
    ],
    [
      'Eichengreens "golden fetters"-these stelt dat de interbellum-goudstandaard de Grote Depressie vooral verergerde via welk mechanisme?',
      [
        'Het dwong deflatoire verkrapping op aan landen die de goudpariteit verdedigden, wat de neergang doorgaf en versterkte over het netwerk van vaste koersen',
        'Het veroorzaakte een eenmalige, permanente instorting van de wereldhandel via identieke op goud gebaseerde tariefschema\'s die door elk lidland gelijktijdig werden opgelegd',
        'Het schafte de wettelijke bevoegdheid van centrale banken af om als lender of last resort op te treden, volledig onafhankelijk van enige pariteitsbinding',
        'Het vereiste dat elke grote munt in 1931 gelijktijdig en met een identieke marge devalueerde, in één gecoördineerde aanpassing',
      ],
      0,
      'Interbellum-goudstandaard: miste flexibiliteit, dwong anticyclische renteverhogingen/deflatie wanneer expansie nodig was. Landen die eerst van goud afstapten (GB 1931) herstelden snelst; "goudblok"-landen (Frankrijk) hadden langste depressies. "Eerder af, eerder herstel"-patroon.',
    ],
    [
      'In het Eaton-Gersovitz-raamwerk (1981) van staatsschuld, wat disciplineert een overheid tegen wanbetaling, gegeven dat staten niet failliet kunnen worden verklaard door een rechtbank?',
      [
        'De dreiging van uitsluiting van toekomstige leningen op internationale kredietmarkten',
        'De automatische inbeslagneming van de vreemde-valutareserves van een in gebreke gebleven land door een internationaal gerechtshof',
        'Een contractuele cross-default-clausule die gelijktijdige versnelling van alle andere uitstaande schuld van het land activeert',
        'Het opleggen van gecoördineerde handelssancties door een supranationale autoriteit bij elke wanbetaling',
      ],
      0,
      'Eaton-Gersovitz: wanbetalingsdiscipline via reputatie-uitsluiting van kredietmarkten. Een staat kiest wanbetaling als voordeel groter is dan verlies van markttoegang. Kredietverstrekkers prijzen schuld op endogene wanbetalingskans. Basis moderne staatsschuld-literatuur.',
    ],
  ],
};

export const MICROECON: CategoryContent = {
  bscba: [
    [
      'Wat beschrijft de wet van afnemende marginale opbrengsten?',
      [
        'Naarmate één input stijgt terwijl anderen vast blijven, daalt de marginale opbrengst ervan uiteindelijk',
        'Totale output van bedrijf moet dalen bij productie-uitbreiding voorbij bepaald punt',
        'Werknemers worden minder productief naarmate ze ervaring op werk opdoen',
        'Toename van alle inputs met hetzelfde percentage vermindert de totale output',
      ],
      0,
      'Afnemende marginale opbrengsten zijn fundamenteel: opeenvolgende eenheden variabele input leveren kleinere output-toenames op.',
    ],
    [
      'In perfecte concurrentie, wat is waar voor lange-termijn evenwicht?',
      [
        'Bedrijven maken nul economische winst en prijs gelijk aan gemiddelde totaalkosten',
        'Bedrijven stellen prijs gelijk aan marginale opbrengst en beperken output voor winstmaximalisatie',
        'Bedrijven rekenen prijs boven gemiddelde kosten om vaste kosten terug te halen',
        'Bedrijven kunnen blijvend positieve winst verdienen ondanks vrije toetreding',
      ],
      0,
      'Nul economische winst kenmerkt lange-termijn concurrentie: prijs gelijk gemiddelde totaalkosten, bedrijven kunnen niet verbeteren.',
    ],
    [
      'Wat is consumentensurplus?',
      [
        'Het verschil tussen wat een koper bereid is te betalen en wat hij werkelijk betaalt',
        'De totale tevredenheid een consument ontvangt van consumptie',
        'Het inkomen dat overblijft nadat een consument voor aankopen betaalt',
        'De winst een consument maakt door te kopen als prijzen dalen',
      ],
      0,
      'Consumentensurplus meet het extra voordeel van een aankoop tegen een prijs lager dan de reservatieprijs van de koper.',
    ],
    [
      'Wat is opportuniteitskosten?',
      [
        'De waarde van het volgende beste alternatief dat wordt opgegeven door iets anders te kiezen',
        'De expliciete kasuitgaven van een aankoop',
        'Het totale bedrag geld aan goed of dienst besteed',
        'De werkelijke kosten plus de tijd besteed aan iets verwerven',
      ],
      0,
      'Opportuniteitskosten liggen ten grondslag aan rationele besluitvorming: iets kiezen betekent de volgende beste optie opgeven.',
    ],
    [
      'Waarom is er deadweight loss in monopolie?',
      [
        'Monopolist produceert minder dan sociaal optimale hoeveelheid, wat wederzijds voordelige ruiltransacties onverwezenlijkt laat',
        'Monopolist moet werknemers meer betalen dan concurrerende bedrijven',
        'Consumenten moeten meer eenheden kopen dan zij willen',
        'Monopolist heeft hogere productiekosten dan concurrerende bedrijven',
      ],
      0,
      'Deadweight loss ontstaat omdat de monopolist de output onder het concurrentieniveau beperkt om de prijs boven de kosten te handhaven.',
    ],
    [
      'Wat is een onherroepelijke kost (sunk cost)?',
      [
        'Een kost die al is gemaakt en niet kan worden teruggekregen',
        'Het totale bedrag aan vaste kapitaal in een bedrijf besteed',
        'Een uitgave die zeker in de toekomst optreedt',
        'De kosten van arbeid die met het productieniveau varieert',
      ],
      0,
      'Onherroepelijke kosten moeten in economische beslissingen worden genegeerd omdat ze ongeacht toekomstige keuzes niet kunnen worden teruggekregen.',
    ],
    [
      'Wat beschrijft comparatief voordeel?',
      [
        'Het vermogen iets tegen lagere opportuniteitskosten dan ander partij te produceren',
        'Het vermogen meer van iets te produceren dan ander partij met dezelfde hulpbronnen',
        'Het voordeel dat groot bedrijf over kleinere concurrenten heeft',
        'De vaardigheid een werknemer door baanspecialisatie ontwikkelt',
      ],
      0,
      'Comparatief voordeel, niet absoluut voordeel, bepaalt specialisatie: partijen moeten specialiseren in wat voor hen relatief goedkoper is.',
    ],
    [
      'Waarom creëren tarieven en quotas deadweight loss?',
      [
        'Ze verhinderen wederzijds voordelige ruiltransacties, waardoor totaal beschikbaar surplus afneemt',
        'Ze verhogen prijs die binnenlandse consumenten voor goederen betalen',
        'Ze beschermen binnenlandse bedrijven tegen concurrentie op kwaliteit',
        'Ze verminderen overheidsbelastinginkomsten',
      ],
      0,
      'Handelsbeperkingen verminderen de hoeveelheid tot onder het efficiënte niveau. Daardoor worden transacties verhinderd die voor beide zijden voordelig zouden zijn geweest.',
    ],
    [
      'Wat vertegenwoordigt een indifferentiecurve?',
      [
        'Alle combinaties van twee goederen die consument dezelfde tevredenheid geven',
        'De budgetbeperking op hoeveel consument kan uitgeven',
        'De afnemende marginaal nut van opeenvolgende eenheden',
        'De afruiling tussen huidig en toekomstig verbruik',
      ],
      0,
      'De indifferentiecurve toont bundels met gelijk nut en helt naar beneden omdat consumenten goederen tegen elkaar afwegen.',
    ],
    [
      'Wat is prijselasticiteit van vraag?',
      [
        'De procentuele verandering van gevraagde hoeveelheid gedeeld door procentuele prijs-verandering',
        'De verandering gevraagde hoeveelheid wanneer prijs met één dollar stijgt',
        'Helling van vraagcurve gemeten in dollars per eenheid',
        'Gevoeligheid van prijs voor veranderingen van consumenteninkomsten',
      ],
      0,
      'Prijselasticiteit meet de gevoeligheid van de koper voor prijsveranderingen: elastisch betekent dat de hoeveelheid sterk verandert.',
    ],
    [
      'Wat is verschil tussen normale en inferieure goederen?',
      [
        'Vraag naar normale goederen stijgt bij inkomenstoename; vraag naar inferieure goederen daalt',
        'Normale goederen zijn noodzakelijkheden en inferieure goederen zijn luxe',
        'Normale goederen worden concurrerend geproduceerd en inferieure door monopolies',
        'Normale goederen hebben elastische vraag en inferieure goederen inelastische vraag',
      ],
      0,
      'Inkomenseffecten onderscheiden deze: bij inkomenstoename kopen consumenten meer normale goederen maar minder inferieure goederen.',
    ],
    [
      'Wat veroorzaakt negatieve externaliteiten?',
      [
        'Een partij buiten transactie draagt kosten die niet in transactieprijs worden weerspiegeld',
        'Bedrijven met schaalvoordelen kunnen prijzen onder concurrentie-niveaus drukken',
        'Werknemers verliezen inkomen wanneer technologie vraag naar hun vaardigheden vermindert',
        'Consumenten moeten belastingen op aankopen van belaste goederen betalen',
      ],
      0,
      'Negatieve externaliteiten treden op wanneer producenten of consumenten kosten aan derden opleggen zonder compensatie.',
    ],
    [
      'Wat onderscheidt vaste van variabele kosten?',
      [
        'Vaste kosten veranderen niet met output; variabele kosten veranderen met geproduceerde hoeveelheid',
        'Vaste kosten gaan naar werknemers en variabele kosten naar leveranciers',
        'Vaste kosten worden eenmalig aan start betaald en variabele kosten continu',
        'Vaste kosten zijn zeker en variabele kosten onzeker',
      ],
      0,
      'Vaste kosten zoals huur veranderen niet met de output; variabele kosten zoals materialen wel met het productievolume.',
    ],
    [
      'Wat is producentensurplus?',
      [
        'Het verschil tussen prijs verkoper ontvangt en minimumprijs die hij accepteert',
        'De totale opbrengsten een bedrijf van productverkoop ontvangt',
        'De winst een bedrijf na betaling van alle kosten verdient',
        'De waarde van onverkochte voorraad aan einde periode',
      ],
      0,
      'Producentensurplus meet het voordeel dat verkopers ontvangen door tegen een prijs boven hun reservatieprijs te verkopen.',
    ],
    [
      'Wat is marktfalen?',
      [
        'Situatie waar vrije markten middelen niet efficiënt toewijzen, vaak door externaliteiten of asymmetrische informatie',
        'Markt die ineenstort wanneer groot bedrijf uit gaat',
        'Situatie waar bedrijven door slecht management niet winstmaximalisatie bereiken',
        'Periode wanneer marktprijzen aanzienlijk onder historische gemiddelden vallen',
      ],
      0,
      'Marktfalen treedt op wanneer ongereguleerde markten geen efficiëntie bereiken—voorbeelden: vervuiling, monopolie, informatieasymmetrie.',
    ],
  ],
  msc: [
    [
      'Waaraan wordt een Pigouviaanse belasting gelijkgesteld?',
      [
        'De marginale externe kost bij het efficiënte niveau van de activiteit',
        'De gemiddelde maatschappelijke kost van de activiteit over alle producenten',
        'Het consumentensurplus dat verloren gaat aan de externaliteit',
        'De totale schade veroorzaakt door de activiteit, verdeeld over producenten',
      ],
      0,
      'Het juiste niveau bepalen is de hele moeilijkheid; Coases bezwaar was dat onderhandeling over eigendomsrechten dezelfde allocatie zou kunnen bereiken zonder belasting.',
    ],
    [
      'Wat vertelt het envelopetheorema?',
      [
        'De afgeleide van de waardefunctie is gelijk aan de partiële afgeleide van de doelfunctie, geëvalueerd in het optimum',
        'Dat indifferentiecurves nooit kunnen kruisen in een inwendig punt',
        'Dat de Slutsky-matrix symmetrisch en negatief semidefiniet is',
        'Dat een beperkt optimum altijd op de rand van de haalbare verzameling moet liggen en nooit strikt binnen',
      ],
      0,
      'Het is waarom de indirecte effecten via de keuzevariabelen genegeerd kunnen worden, en dat is precies wat comparatieve statica hanteerbaar maakt.',
    ],
    [
      'Wat stelt het Coase-theorema, en onder welke voorwaarden?',
      [
        'Een efficiënte allocatie wordt bereikt ongeacht de initiële rechten',
        'Externaliteiten worden altijd het best gecorrigeerd door belasting',
        'Bedrijven bestaan omdat markttransacties altijd goedkoper zijn dan interne transacties',
        'Efficiënte uitkomsten vereisen een gelijke initiële verdeling van rechten',
      ],
      0,
      'De voorwaarden gelden nooit, en dat is precies het punt: Coase betoogde dat transactiekosten zijn waar het werkelijk om draait.',
    ],
    [
      'Wat is het verschil tussen Marshalliaanse en Hicksiaanse vraag?',
      [
        'Marshalliaans houdt inkomen constant; Hicksiaans houdt nut constant en isoleert zo het substitutie-effect',
        'Marshalliaans geldt voor normale goederen en Hicksiaans voor inferieure goederen',
        'Marshalliaans is afgeleid uit onthulde voorkeuren en Hicksiaans uit kardinaal nut',
        'Marshalliaans is kort-termijn en Hicksiaans lang-termijn',
      ],
      0,
      'De Slutsky-decompositie is precies het verschil tussen beide, en het lemma van Shephard is hoe je van het ene naar het andere probleem beweegt.',
    ],
    [
      'Wat schrijft de Ramsey-regel voor bij optimale goederenbelasting?',
      [
        'Belast goederen omgekeerd proportioneel met hun elasticiteit, om het welvaartsverlies bij gegeven opbrengst te minimaliseren',
        'Belast alle goederen tegen een uniform tarief om relatieve prijzen niet te vertekenen',
        'Belast alleen finale consumptie en nooit intermediaire goederen, zodat geen cascade van belasting door de keten ontstaat',
        'Belast inkomen in plaats van goederen waar administratief haalbaar',
      ],
      0,
      'Het is efficiënt en regressief, en dat is de spanning waarmee elk echt belastingstelsel onderhandelt.',
    ],
    [
      'Wat stelt het Modigliani-Miller-theorema in zijn eerste propositie?',
      [
        'In frictieloze markten is de waarde van een bedrijf onafhankelijk van de kapitaalstructuur',
        'Dividendbeleid bepaalt de bedrijfswaarde onder zekerheid',
        'Leverage verlaagt de gewogen gemiddelde kapitaalkosten monotoon',
        'Aandeelhouders en schuldhouders vereisen in evenwicht hetzelfde rendement',
      ],
      0,
      'De waarde ervan zit in het feit dat het als boekhoudkundige identiteit laat zien wat er wél toe moet doen: belastingen, faillissementskosten, agency en informatie, omdat structuur zelf er niet toe doet.',
    ],
    [
      'Wat is een Nash-evenwicht?',
      [
        'Een strategieprofiel waarin geen speler kan winnen door eenzijdig af te wijken',
        'Een uitkomst die de som van de payoffs van alle spelers maximaliseert',
        'Een strategieprofiel dat optimaal is tegen elke mogelijke tegenstanderstrategie',
        'Een uitkomst waarvan geen coalitie profijtelijk kan afwijken',
      ],
      0,
      'De coalitie-benadering is de kern uit coöperatieve speltheorie. Dominante strategieën zijn de sterkste voorwaarde. Nash-evenwicht vereist geen eenzijdige afwijking voordelig is.',
    ],
    [
      'Aan wat moet een instrumentele variabele voldoen?',
      [
        'Relevantie voor de endogene regressor en exclusie uit de uitkomstvergelijking behalve via die regressor',
        'Sterke correlatie met de uitkomstvariabele, zodat de eerste fase het grootste deel van de variantie ervan verklaart',
        'Onafhankelijkheid van alle andere regressoren in het model',
        'Normaliteit van haar verdeling in de populatie',
      ],
      0,
      'De exclusierestrictie is niet-toetsbaar en is waar bijna elke discussie over een IV-paper zich in de praktijk afspeelt.',
    ],
    [
      'In het klassieke Bertrand-model van prijsconcurrentie tussen twee bedrijven die een homogeen goed verkopen tegen identieke constante marginale kosten, wat is de Nash-evenwichtsuitkomst?',
      [
        'Beide bedrijven prijzen tegen marginale kosten en maken nul economische winst, ook al zijn er maar twee bedrijven op de markt',
        'Bedrijven convergeren naar de monopolieprijs via stilzwijgende collusie, omdat een rivaal onderbieden nooit individueel winstgevend is',
        'De prijs komt te liggen op het gemiddelde van marginale kosten en de monopolieprijs, verdeeld naar het marktaandeel van elk bedrijf',
        'De markt bereikt nooit een evenwicht, omdat elk bedrijf een blijvende prikkel houdt om welke prijs de ander ook rekent te onderbieden',
      ],
      0,
      'Bertrand duopolie met homogeen product en identieke kosten: prijs = marginale kosten - competitieve uitkomst ondanks twee bedrijven. Paradox tegenover intuïtie. Echte markten vermijden via capaciteit, productdifferentiatie of collusie.',
    ],
    [
      'In een Cournot-oligopolie met n identieke bedrijven, constante marginale kosten en lineaire vraag, wat gebeurt er met de evenwichtsmarge boven kosten (de Lerner-index) als het aantal bedrijven n groot wordt?',
      [
        'Ze convergeert naar nul, zodat de marktuitkomst de competitieve benchmark benadert',
        'Ze convergeert naar een positieve constante gelijk aan het vaste-kostenaandeel van de bedrijfstak in de omzet',
        'Ze blijft vast op een half ongeacht n, omdat Cournot-evenwichten schaalinvariant zijn',
        'Ze stijgt met n, omdat de residuele vraag van elk bedrijf minder elastisch wordt als rivalen zich vermenigvuldigen',
      ],
      0,
      'Cournot: marge = 1/(n × elasticiteit). Marge → 0 als n groot. Lerner-index = HHI/elasticiteit - brug tussen theorie en mededinging. Cournot (1838) introduceerde reactiefunctie voor Nash formaliseerde evenwicht.',
    ],
    [
      'Bij derdegraads prijsdiscriminatie tussen marktsegmenten die niet aan elkaar kunnen doorverkopen, wat bepaalt de relatieve prijzen die een winstmaximaliserende monopolist rekent?',
      [
        'Het segment met minder elastische vraag krijgt een hogere marge boven marginale kosten, omgekeerd proportioneel met zijn elasticiteit',
        'De prijs wordt gelijkgetrokken over segmenten wanneer de monopolist gezamenlijke winst maximaliseert, omdat maar één marginale kost geldt voor beide',
        'De prijs wordt vastgesteld in proportie tot het gemiddelde inkomen van elk segment, volgens een betaalcapaciteitsregel',
        'De prijs is het hoogst in welk segment dan ook met het grootste aantal kopers, ongeacht hoe elastisch hun vraag is',
      ],
      0,
      'Derdegraads discriminatie: marge = 1/elasticiteit per segment. Minder elastische vraag → hogere marge. Robinson (1933). Vb: business vs toeristen klasse. Kan welvaart verlagen ondanks hogere winst.',
    ],
    [
      'In Akerlofs "market for lemons", welk mechanisme kan ervoor zorgen dat de markt voor tweedehands auto\'s instort totdat alleen nog auto\'s van lage kwaliteit worden verhandeld?',
      [
        'Verkopers kennen privé de kwaliteit van een auto maar kopers niet, zodat kopers een prijs bieden die alleen de gemiddelde kwaliteit weerspiegelt, wat verkopers van bovengemiddelde auto\'s uit de markt drijft',
        'Koopers hebben marktmacht en zetten een prijs onder de marginale productiekost van zelfs de laagste-kwaliteitsauto\'s',
        'Zoekfricties en kostbare informatievergaring maken het onbetaalbaar duur voor kopers om ooit de eigenaren van hoge-kwaliteitsauto\'s te vinden in een grote, anonieme tweedehandsmarkt',
        'Een bindend prijsplafond opgelegd door toezichthouders ligt onder de reserveringswaarde die eigenaren van goede auto\'s vereisen om te verkopen',
      ],
      0,
      'Asymmetrische informatie: kopers betalen voor gemiddelde kwaliteit. Hoge-kwaliteit eigenaren trekken zich terug; gemiddelde daalt - markt verhandelt alleen "citroenen". Akerlof (1970) toonde asymmetrie kan handel vernietigen.',
    ],
    [
      'In Spence\'s job-market-signaleringsmodel, wat maakt onderwijs een geloofwaardig signaal van de onwaargenomen bekwaamheid van een werknemer, ook als onderwijs zelf niets toevoegt aan productiviteit?',
      [
        'Onderwijs is goedkoper te verkrijgen voor bekwame werknemers dan voor minder bekwame, wat voldoet aan een single-crossing-voorwaarde die een scheidend evenwicht ondersteunt',
        'Onderwijs verhoogt mechanisch de gemeten output op het werk voor elke werknemer in exact dezelfde mate, zodat de rol ervan als signaal niet te onderscheiden is van haar rol als menselijk kapitaal',
        'Bedrijven kunnen ware bekwaamheid kosteloos verifiëren zodra een diploma wordt ingeleverd, wat het signaal puur ceremonieel maakt',
        'Minder bekwame werknemers is wettelijk verboden om de kwalificatie te verkrijgen, wat de loonkloof tussen groepen in stand houdt',
      ],
      0,
      'Spence signaling: onderwijs goedkoper voor bekwamen (single-crossing). Bekwamen signaleren via scholing, minder bekwamen niet. Bedrijven leiden type uit kwalificatie af. Meerdere evenwichten mogelijk. Nobelprijs 2001 (Akerlof, Stiglitz).',
    ],
    [
      'De Nash-onderhandelingsoplossing selecteert de individueel rationele, Pareto-efficiënte overeenkomst die welke grootheid maximaliseert?',
      [
        'Het product van de winst van elke partij in nut boven hun onenigheidspunt (dreigpunt)-uitbetaling, bekend als het Nash-product',
        'De ongewogen som van de uitbetalingen van beide partijen, onderworpen aan een gelijke verdeling van elk surplus boven onenigheid',
        'Het minimum van de uitbetalingen van de twee partijen relatief aan hun onenigheidspunt, wat een strikt egalitaire verdeling van het gehele onderhandelingssurplus garandeert',
        'De uitbetaling van welke partij dan ook met de sterkere alternatieve optie, alleen gewogen naar relatieve risicoaversie',
      ],
      0,
      'Nash (1950): product van winsten boven onenigheidspunt maximaliseert unieke oplossing. Vier axioma\'s (Pareto, symmetrie, affiene invariantie, onafhankelijkheid van irrelevante alternatieven). Rubinsteins alternerend-bod model (1982) convergeert ernaar als verdiscontering verdwijnt.',
    ],
    [
      'De eerste hoofdstelling van de welvaartseconomie stelt dat, onder standaardvoorwaarden (geen externaliteiten, complete markten, prijsnemende actoren), elke competitieve-evenwichtsallocatie wat is?',
      [
        'Pareto-efficiënt',
        'Utilitair-optimaal, in de zin dat het de ongewogen som van het nut van elk individu maximaliseert',
        'Rechtvaardig, in de zin dat geen consument het bundel benijdt dat aan een andere consument is toegewezen',
        'Uniek bepaald door de initiële toewijzing, zodat het resulterende evenwicht altijd uniek is',
      ],
      0,
      'Competitief evenwicht waste geen kans op wederzijds voordeel: Pareto-efficiënt. Zwijgt over eerlijkheid - ongelijke toewijzing ook efficiënt. Arrow-Debreu-raamwerk. Niet te verwarren met "goed": een evenwicht kan alles aan één iemand geven.',
    ],
  ],
  phd: [
    [
      'Wat vestigt het onmogelijkheidstheorema van Arrow?',
      [
        'Geen rangordestemregel voldoet gelijktijdig aan onbeperkt domein, Pareto, niet-dictatuur en onafhankelijkheid van irrelevante alternatieven',
        'Geen stemregel kan strategievast zijn voor meer dan twee alternatieven',
        'Meerderheidsregel cirkelt altijd wanneer voorkeuren enkeltoppig zijn',
        'Maatschappelijke welvaart kan niet vergeleken worden tussen individuen, zodat elke aggregatie van ordinale voorkeuren naar een collectieve rangorde betekenisloos is',
      ],
      0,
      'Het strategievastheidsresultaat is Gibbard-Satterthwaite, en enkeltoppigheid is precies de domeinbeperking die aan Arrow ontsnapt.',
    ],
    [
      'Wat stelt het opbrengstequivalentietheorema?',
      [
        'Standaardveilingen met symmetrische, onafhankelijke private waarden leveren dezelfde verwachte opbrengst op',
        'Alle veilingvormen zijn efficiënt onder private waarden',
        'Verzegelde en open veilingen leveren identieke biedingen op',
        'Reserveringsprijzen beïnvloeden de verwachte opbrengst niet, zodat het instellen ervan nooit het risico van geen verkoop waard is',
      ],
      0,
      'Elke aanname erin is waar veilingontwerp over vecht: affiliatie, asymmetrie en risicoaversie breken het elk in een andere richting.',
    ],
    [
      'Wat is de "Diamond-paradox" in zoektheorie?',
      [
        'Met elke positieve zoekkost en homogene verkopers is het unieke evenwichtsprijs de monopolieprijs',
        'Zoekfricties verlagen prijzen altijd ten opzichte van het Walrasiaans evenwicht',
        'Consumenten zoeken oneindig lang wanneer prijzen gespreid zijn, zodat geen evenwicht met positieve zoekkost bestaat',
        'Prijsspreiding kan niet bestaan in evenwicht met identieke goederen',
      ],
      0,
      'Het is waarom de literatuur heterogeniteit of ruis moest invoeren om de prijsspreiding te krijgen die daadwerkelijk wordt waargenomen.',
    ],
    [
      'Wat is het verschil tussen de intensieve en extensieve marge van arbeidsaanbod?',
      [
        'Gewerkte uren door deelnemers, tegenover de beslissing om überhaupt deel te nemen',
        'Voltijd tegenover deeltijd werk',
        'Loondienst tegenover zelfstandig werk',
        'Kortetermijn- tegenover langetermijnelasticiteit, gemeten over een jaar en over een werkzaam leven',
      ],
      0,
      'De extensieve marge draagt het grootste deel van de aggregate elasticiteit, wat is waarom modellen die op micro-intensievemargeschattingen gecalibreerd zijn de arbeidsdynamiek verkeerd krijgen.',
    ],
    [
      'Wat verklaart "Baumols kostenziekte"?',
      [
        'Relatieve prijsstijgingen in sectoren met trage productiviteitsgroei, omdat lonen het economiewijde gemiddelde volgen',
        'De neiging van overheidskosten om sneller te stijgen dan de private sector door een politiek mechanisme',
        'Het inflatoire gevolg van monopolieprijsvorming in diensten',
        'De daling van industriële werkgelegenheid met inkomen',
      ],
      0,
      'Het is waarom gezondheidszorg, onderwijs en live-optredens relatief duurder worden zonder dat iemand er minder efficiënt in wordt.',
    ],
    [
      'Wat benut het "regressiediscontinuïteits"-ontwerp?',
      [
        'Quasi-willekeurige toewijzing rond een drempel in een toewijzingsvariabele',
        'Willekeurige variatie in een instrument dat de behandelingskans verschuift',
        'Herhaalde observatie van dezelfde eenheden voor en na behandeling',
        'Matchen van behandelde en controle-eenheden op waargenomen kenmerken',
      ],
      0,
      'De identificatie is lokaal aan de drempel, en dat is zowel de kracht ervan als de grens van wat het over iemand anders kan vertellen.',
    ],
    [
      'In het Rothschild-Stiglitz-model (1976) van competitieve verzekeringsmarkten met adverse selectie, wat kenmerkt het evenwicht wanneer er een bestaat?',
      [
        'Een scheidend evenwicht: hoog-risicotypes koopen volledige dekking, en laag-risicotypes krijgen alleen partiële dekking die hoog-risicotypes ervan weerhoudt zich als laag-risico voor te doen',
        'Een enkel poolend contract dat beide risicotypes koopen tegen één actuarieel eerlijke gemiddelde premie',
        'Volledige dekking voor beide types, elk geprijsd tegen dat types eigen actuarieel eerlijke premie, omdat verzekeraars risico kosteloos verifiëren',
        'Er wordt tegen geen enkele prijs verzekering aangeboden, omdat competitieve verzekeraars niet kunnen breakeven zodra adverse selectie aanwezig is',
      ],
      0,
      'Scheiding in evenwicht: hoog-risico krijgt volledige dekking, laag-risico partiële. Rival kan poolend contract niet ondermijnen. Voor bepaalde parameters geen evenwicht. Nobelprijs 2001 (Stiglitz).',
    ],
    [
      'Wat vestigt het Myerson-Satterthwaite-theorema (1983) als onmogelijkheidsresultaat voor bilaterale handel onder private informatie?',
      [
        'Geen mechanisme kan gelijktijdig efficiënt, individueel rationeel en budgetneutraal zijn wanneer koper- en verkoperwaarderingen privé zijn en hun mogelijke bereiken overlappen',
        'Geen mechanisme kan gelijktijdig prikkelcompatibel en individueel rationeel zijn zodra één verkoper twee of meer kopers heeft wier private waarderingen onderling gecorreleerd zijn in plaats van onafhankelijk getrokken',
        'Efficiënte handel vereist een externe subsidie zodra meer dan twee handelaren gelijktijdig deelnemen aan de markt',
        'Ex-post efficiëntie wordt alleen onhaalbaar wanneer waarderingen getrokken worden uit verdelingen die asymmetrisch zijn tussen de twee marktzijden',
      ],
      0,
      'Tweezijdig: geen mechanisme kan efficiëntie, prikkelcompatibiliteit en budgetneutraliteit tegelijk bereiken bij overlappende waarderingsbereiken. Anders dan eenzijdig geval. Nobelprijs 2007 (Myerson).',
    ],
    [
      'In een common-value-veiling beschrijft de "winnaarsvloek" welk systematisch patroon voor kopers die er geen rekening mee houden?',
      [
        'Het winnende bod overschat de werkelijke gemeenschappelijke waarde, omdat winnen zelf informatief is dat de winnaar de meest optimistische schatting had',
        'De winnende bieder in een verzegelde eersteprijsveiling betaalt altijd strikt meer dan het tweede-hoogste ingediende bod',
        'Bieders met enige private informatie overbieden altijd degenen zonder, ongeacht de omvang van beide groepen waarderingsschattingen',
        'Veilingopbrengst valt systematisch en voorspelbaar onder de privé bekende reserveringsprijs van de verkoper zodra het aantal concurrerende bieders volledig onbeperkt is',
      ],
      0,
      'Winnen signaleert dat je schatting uit de bovenste staart stamde. Rationele bieders corrigeren naar beneden. Naïeve bieders betalen te veel. Empirisch getoond door Capen et al. (1971). Wilson (1977) gaf theoretische basis.',
    ],
    [
      'Het Vickrey-Clarke-Groves-mechanisme (VCG) bereikt efficiënte voorziening van een collectief goed met privé bekende waarderingen door wat te doen?',
      [
        'Elke actor een betaling in rekening brengen gelijk aan de externaliteit die zijn gerapporteerde waardering aan alle anderen oplegt, wat waarheidsgetrouw rapporteren een dominante strategie maakt',
        'Het recht om het voorzieningsniveau te bepalen veilen aan de enkele hoogste bieder onder alle getroffen actoren',
        'Unanieme, onafhankelijk geverifieerde toestemming van elke getroffen actor vereisen voordat enig positief niveau van het collectieve goed ooit geautoriseerd of voorzien kan worden',
        'De betaling van elke actor gelijkstellen aan zijn eigen gerapporteerde waardering, volledig terugbetaald wanneer het goed uiteindelijk niet wordt voorzien',
      ],
      0,
      'Clarke-pivot regel: betaling = externaliteit op anderen. Dominante strategie voor waarheid. Groves-klasse. Niet budgetneutraal in het algemeen. Online advertenties gebruiken generalized-second-price als praktische variant.',
    ],
    [
      'Het volkstheorema voor oneindig herhaalde spellen stelt dat, naarmate de discontovoet van spelers naar één neigt, welke verzameling uitkomsten ondersteund kan worden als subgame-perfecte evenwichten?',
      [
        'Praktisch elke individueel rationele, haalbare uitbetalingsvector, inclusief coöperatieve uitkomsten die geen evenwicht zijn van het eenmalige stagespel',
        'Alleen het unieke Nash-evenwicht van het stagespel, identiek gespeeld in elke periode',
        'De Pareto-efficiënte uitkomst uniek, omdat voldoende geduldige spelers altijd hierop coördineren',
        'Elke uitkomst dan ook, omdat verdiscontering die naar één neigt alle beperkingen op haalbare strategieën wegneemt',
      ],
      0,
      'Triggerstrategieën: samenwerk zolang ander doet, straf eeuwig na afwijking. Individueel rationeel en haalbaar → ondersteund. Fudenberg-Maskin (1986). Verklaart stilzwijgende collusie bij voldoende geduld.',
    ],
    [
      'Het Bulow-Klemperer-resultaat (1996) vergelijkt eenvoudige veilingen met geoptimaliseerde onderhandeling, en concludeert wat over het aantrekken van één extra bieder?',
      [
        'Eén bieder toevoegen aan een gewone English-veiling zonder reserveringsprijs verslaat het optimaliseren van de reserveringsprijs voor de oorspronkelijke groep bieders, in verwachte opbrengst',
        'Onderhandelde private verkopen via een bekwame tussenpersoon domineren betrouwbaar open veilingen in verwachte opbrengst zodra meer dan twee serieuze bieders op de markt zijn',
        'Bieders toevoegen aan een verzegelde eersteprijsveiling verlaagt de verwachte opbrengst zodra bieders risicoavers zijn',
        'Optimale reserveringsprijzen worden volledig overbodig zodra het aantal bieders drie overschrijdt, voor elke waarderingsverdeling',
      ],
      0,
      'Voeg één bieder toe aan eenvoudige veiling: beter dan optimale reserveringsprijs tunen. Concurrentie werkt harder dan mechanismeontwerp. Praktisch toegepast in spectrumveilingen.',
    ],
    [
      'Het Gale-Shapley-deferred-acceptance-algoritme, wanneer de ene zijde de voorstellen doet, produceert een stabiele matching met welke strategische eigenschap voor de voorstellende zijde?',
      [
        'Waarheidsgetrouw rapporteren is een dominante strategie voor de voorstellende zijde, omdat het algoritme hun zwak-beste stabiele matching oplevert',
        'Waarheidsgetrouw rapporteren is een dominante strategie voor beide zijden gelijktijdig, ongeacht welke zijde voorstelt',
        'Het algoritme produceert altijd de unieke stabiele matching die bestaat voor een gegeven verzameling voorkeuren',
        'Stabiliteit vereist dat beide zijden volledige en volledig waarheidsgetrouwe voorkeursrangschikkingen over elke mogelijke partner indienen, anders kan geen stabiele matching gegarandeerd worden',
      ],
      0,
      'Voorstellende zijde: dominante strategie om waarheid te zeggen. Algoritme geeft hun zwak-best stabiel evenwicht. Ontvangende zijde kan profiteren van leugen. Nobelprijs 2012 (Roth).',
    ],
    [
      'De Coase-conjectuur betreft een monopolist van duurzame goederen die zijn prijs frequent kan herzien in de tijd. Wat voorspelt deze naarmate het interval tussen prijsherzieningen naar nul krimpt?',
      [
        'De prijs convergeert naar marginale kosten en de monopoliewinst stort in richting de competitieve uitkomst, omdat kopers rationeel wachten op toekomstige prijsverlagingen',
        'De prijs stijgt onbegrensd terwijl de monopolist steeds meer surplus onttrekt aan ongeduldige kopers',
        'De winst convergeert altijd naar exact het standaard statische monopolieniveau ongeacht het herzieningsinterval, omdat bindingsproblemen van dit soort alleen bindend zijn in modellen met een strikt eindige tijdshorizon',
        'De optimale strategie van de monopolist convergeert naar verhuren in plaats van uitverkopen, wat de statische monopoliewinst volledig herstelt',
      ],
      0,
      'Monopolist concurreert met eigen toekomstig zelf: kopers wachten op prijsdalingen. Prijs→marginale kosten als herzieningstermijn→0. Formalisering: Stokey, Bulow, Gul-Sonnenschein-Wilson.',
    ],
    [
      'Holmstroms informativiteitsprincipe (1979), in principaal-agentmodellen van moral hazard, stelt dat een optimaal prikkelcontract positief gewicht moet leggen op welke signalen?',
      [
        'Elk signaal, hoe ruizig ook, dat niet-triviale statistische informatie over de inspanning van de agent draagt boven wat andere reeds gebruikte signalen al vangen',
        'Alleen het uiteindelijke outputniveau, omdat het conditioneren van betaling op enig ander tussenliggend signaal eenvoudigweg extra risico toevoegt voor een risicoaverse agent zonder compenserend prikkelvoordeel',
        'De inspanning van de agent rechtstreeks, mits de principaal een voldoende precieze monitoringtechnologie installeert',
        'Een vast loon dat elk signaal negeert, wanneer de agent risiconeutraal is en de principaal risicoavers',
      ],
      0,
      'Elk informatief signaal helpt afweging tussen prikkels en verzekering, zelfs als zwak gecorreleerd. Basis voor relatieve prestatie: salarisbeschrijving netto sectorindex. Nobelprijs 2016 (Holmstrom).',
    ],
  ],
  professor: [
    [
      'Wat impliceert het Diamond-Mirrlees-productie-efficiëntietheorema voor belastingontwerp?',
      [
        'Intermediaire goederen zouden niet belast moeten worden als de overheid finale consumptie kan belasten, ook in een second-best wereld',
        'Alle goederen zouden tegen een uniform tarief belast moeten worden',
        'Productie-efficiëntie vereist nulbelasting op kapitaalinkomen',
        'Optimale belastingen zijn altijd proportioneel met inkomen, zodat een vlak tarief elk niet-lineair schema domineert op efficiëntiegronden',
      ],
      0,
      'Het is het theoretische argument tegen omzetbelastingen en voor btw-achtige stelsels met volledige aftrek van voorbelasting.',
    ],
    [
      'Wat is de conclusie van het Atkinson-Stiglitz-theorema, en de kritische aanname?',
      [
        'Met separabele voorkeuren en optimale niet-lineaire inkomstenbelasting voegen differentiële goederenbelastingen niets toe - separabiliteit is de aanname',
        'Kapitaalinkomen zou nooit belast moeten worden onder welke voorkeuren dan ook, omdat de intertemporele vertekening altijd de distributieve winst overtreft',
        'Uniforme goederenbelasting is optimaal ongeacht voorkeuren',
        'Inkomstenbelasting domineert consumptiebelasting in alle gevallen',
      ],
      0,
      'Het loslaten van separabiliteit - zoals wanneer vrije tijd complementair is aan bepaalde goederen - is wat differentiële belastingen en kapitaalbelasting terugbrengt.',
    ],
    [
      'Wat doet de "sufficient statistic"-benadering in de publieke economie?',
      [
        'Leidt welvaartsconclusies af uit schatbare elasticiteiten',
        'Reduceert een structureel model tot zijn reduced form voor voorspelling',
        'Schat alle structurele parameters uit één enkele momentvoorwaarde',
        'Vervangt een volledige welvaartsfunctie door een samenvattende index',
      ],
      0,
      'Chetty\'s formulering. Het koopt robuustheid ten aanzien van niet-gemodelleerde details, ten koste van zwijgen over counterfactuals ver van de data.',
    ],
    [
      'Wat betoogt onderzoek naar "superstar-effecten" op bedrijfsniveau over factoraandelen?',
      [
        'Herallocatie richting bedrijven met hoge marges en laag arbeidsaandeel verklaart een groot deel van de daling van het aggregate arbeidsaandeel',
        'De daling is volledig verklaard door kapitaalbevooroordeelde technische verandering binnen bedrijven',
        'Gemeten dalingen zijn een artefact van alleen verkeerd gemeten immateriële kapitaal',
        'Arbeidsaandelen zijn niet gedaald zodra huisvesting wordt uitgesloten, zodat de hele literatuur op geïmputeerde huurwaarden berust',
      ],
      0,
      'Autor et al. en De Loecker et al. Verkeerde meting van immateriële activa is een reëel en apart bezwaar dat de vraag niet heeft beslecht.',
    ],
    [
      'Wat is de huidige stand van de literatuur over minimumloon en werkgelegenheid?',
      [
        'Gematigde verhogingen laten kleine werkgelegenheidseffecten zien in de meeste geloofwaardige onderzoeksopzetten',
        'De competitieve voorspelling van substantieel banenverlies wordt uniform bevestigd',
        'Alle studies vinden nu werkgelegenheidswinsten door minimumloonverhogingen',
        'De literatuur is verlaten als niet-identificeerbaar',
      ],
      0,
      'Card-Krueger tot en met Cengiz et al. tegenover Neumark en Wascher. Monopsonie is het theoretische kader dat een klein effect niet-verrassend maakt.',
    ],
    [
      'Myersons optimale-veilingtheorie (1981) toont dat een opbrengstmaximaliserende verkoper met bieders met onafhankelijke private waarden biedingen moet rangschikken volgens wat, in plaats van de ruwe biedgrootte?',
      [
        'De "virtuele waardering" van elke bieder - waarde minus een inverse-hazardvoetcorrectieterm - die alleen samenvalt met de ruwe waarde onder een regelmatigheidsvoorwaarde op de verdeling',
        'De waardering van de bieder netto de eigen reserveringskost van de verkoper, identiek berekend voor elke bieder ongeacht diens verdeling',
        'Het ex-postsurplus dat een bieder zou verdienen als het goed precies tegen zijn eigen ingediende bod zou worden toegewezen',
        'Een gewogen gemiddelde van het eigen bod van de bieder en het tweede-hoogste rivaliserende bod, met gewichten bepaald door de risicoaversie van elke bieder',
      ],
      0,
      'Virtuele waardering = v - (1-F(v))/f(v). Rangschik en wijs toe naar virtueel, niet waardering. Asymmetrische bieders: optimaal goed aan laagste-waardering bieder. Nobelprijs 2007 (Myerson).',
    ],
    [
      'Het simultane-oplopende-veilingontwerp gebruikt voor FCC-spectrumverkoop, ontwikkeld met inbreng van Milgrom en Wilson, werd vooral gekozen om welk probleem op te lossen ten opzichte van sequentiële of verzegelde verkoop van licenties?',
      [
        'Het laat bieders prijzen over licenties in real time volgen en biedingen verschuiven tussen substituut- of complementaire licenties, wat het blootstellingsprobleem vermijdt van vroeg vastleggen op slechts één licentie',
        'Het garandeert volledige allocatieve efficiëntie, omdat het wiskundig equivalent is aan een Vickrey-Clarke-Groves-mechanisme uitgebreid naar meerdere goederen',
        'Het elimineert collusief bieden volledig, omdat bieders structureel worden verhinderd onder de regels van de veiling om enig rivaliserend bod waar te nemen voordat de veiling formeel sluit en de resultaten bekend worden',
        'Het maximaliseert overheidsopbrengst door elke bieder te dwingen één verzegeld bod voor alle licenties tegelijk in te dienen',
      ],
      0,
      'Simultaan oplopend ontwerp laat bieders prijzen en bundels in real-time aanpassen: vermijdt blootstellingsprobleem. Niet letterlijk VCG. Bieders signaleerden via cijferpatronen. 2016-17 incentive auction herstructureerde spectrum.',
    ],
    [
      'In het Baron-Myerson-model (1982) van optimale regulering van een monopolist met privé bekende productiekosten, welke vertekening laat de optimale regel van de regulator bestaan ten opzichte van prijsstelling tegen marginale kosten?',
      [
        'Output wordt vastgesteld onder het efficiënte marginale-kostenniveau voor bedrijfstypes met hogere kosten, om de informatierente te verminderen die aan types met lagere kosten moet worden toegekend',
        'Output wordt voor elk kostentype precies op marginale kosten vastgesteld, in plaats gefinancierd door een lump-sum-overdracht van belastingbetalers',
        'De prijs wordt begrensd op de gemiddelde kosten van het minst efficiënte type dat de regulator plausibel acht',
        'De regulator reproduceert eenvoudigweg de ongereguleerde monopolieuitkomst in elk geval, omdat asymmetrische informatie over de werkelijke kosten van het bedrijf elke welvaartsverhogende interventie volkomen nutteloos maakt',
      ],
      0,
      'Asymmetrische kosten: "geen vertekening aan de top" (efficiënt type produceert efficiënt), onderproductie elders (rente beperken). Patroon universeel in mechanismeontwerp. Basis Laffont-Tirole reguleringstheorie.',
    ],
    [
      'De Grossman-Hart-Moore-eigendomsrechtentheorie van het bedrijf verklaart de grens tussen verticale integratie en arm\'s-length-contractering met een beroep op wat, gegeven dat contracten noodzakelijkerwijs incompleet zijn?',
      [
        'Eigendom van niet-menselijke activa bepaalt de onderhandelingsmacht en alternatieve optie van elke partij bij toekomstige heronderhandeling, wat de ex-anteprikkels vormt om relatiespecifieke investeringen te doen',
        'De relatieve omvang van de transactiekost van het opstellen van een volledig compleet contract, een kost die voldoende zorgvuldige en goed betaalde juridische opstelling in principe altijd naar nul kan drijven met genoeg tijd en geld',
        'Verschillen in risicoaversie tussen de contracterende partijen, waarbij de meer risicoaverse partij altijd eindigt als de residuele claimant',
        'Het belastingvoordeel van verticale integratie ten opzichte van arm\'s-length-contractering onder een standaard vennootschapsbelastingcode',
      ],
      0,
      'Onvolledige contracten: eigendom bepaalt heronderhandelingskracht (toegang weigeren). Vormt prikkels voor relatiespecifieke investeringen. Integratiekeuze bepaald door residuele controlerechten. Nobelprijs 2016 (Hart).',
    ],
    [
      'In Mirrlees\'s model van optimale niet-lineaire inkomstenbelasting (1971) met onwaarneembare bekwaamheid, wat is het beroemde resultaat voor het marginale belastingtarief aan de absolute top van een begrensde bekwaamheidsverdeling?',
      [
        'Het is nul aan de top, omdat het vertekenen van het arbeidsaanbod van het hoogst bekwame type geen compenserende winst oplevert in het afschrikken van nabootsing door iemand boven hem',
        'Het is gelijk aan het opbrengstmaximaliserende tarief geïmpliceerd door de Laffer-curve, een waarde onafhankelijk van de vorm van de bekwaamheidsverdeling',
        'Het stijgt onbegrensd naarmate inkomen de top nadert, om herverdeling van de rijksten te maximaliseren',
        'Het wordt gelijkgesteld aan het economiewijde gemiddelde belastingtarief, om algehele opbrengstneutraliteit te behouden',
      ],
      0,
      'Begrensde bekwaamheid: marginaal tarief = nul aan top (geen prikkelcompatibiliteitsbeperking boven). Fragiel: Pareto-staart (onbegrensd) → nul kan omkeren (Saez 2001). Nobelprijs 1996 (Mirrlees).',
    ],
    [
      'In het Shapiro-Stiglitz-efficiëntielonenmodel (1984), waarom kenmerkt onvrijwillige werkloosheid het arbeidsmarktevenwicht ook al zijn lonen perfect flexibel?',
      [
        'Bedrijven betalen boven het marktclearende loon zodat baanverlies kostbaar is, wat lijntrekken ontmoedigt dat niet perfect gemonitord kan worden; het evenwichtsniveau van werkloosheid is wat die dreiging economiewijd geloofwaardig maakt',
        'Vakbonden onderhandelen lonen boven het competitieve niveau en rantsoeneren dan de resulterende schaarse banen onder hun leden via anciënniteitsregels',
        'Minimumloonwetgeving wordt boven het marktclearende loon vastgesteld in elk rechtsgebied dat het model formeel beschouwt, wat het specifieke institutionele mechanisme is waarop het model overal leunt om de werkloosheid te genereren die het voorspelt',
        'Bedrijven mogen wettelijk geen nominale lonen verlagen onder een psychologisch belangrijk referentiepunt vastgesteld door het vorige contract',
      ],
      0,
      'Inspanning niet perfect monitord: bedrijven betalen boven marktclearing (baanverlies kostbaar). Allemaal betalen evenzeer → evenwichtswerkloosheid maakt "no-shirking" geloofwaardig. Nobelprijs 2001 (Stiglitz).',
    ],
    [
      'Hotellings lineaire-stadmodel van ruimtelijke concurrentie (1929) wordt populair gecrediteerd met een "principe van minimale differentiatie". Wat toonde latere formele analyse van het volledige tweetraps-locatie-dan-prijsspel werkelijk aan?',
      [
        'Bedrijven vestigen zich in evenwicht daadwerkelijk aan de twee uitersten van de lijn, niet in het midden, zodra prijsconcurrentie correct wordt gemodelleerd (d\'Aspremont, Gabszewicz en Thisse, 1979)',
        'Bedrijven vestigen zich altijd precies op het middelpunt van de lijn, wat Hotellings eigen beweerde evenwicht bevestigt onder elke specificatie van transportkosten',
        'Bedrijven vestigen zich willekeurig langs de lijn, omdat locatie in het lineaire model in essentie geen effect heeft op daaropvolgende evenwichtsprijzen',
        'Bedrijven vestigen zich op de twee kwartielpunten van de lijn in evenwicht, wat de resulterende markt splitst in vier gelijke en perfect symmetrische geografische segmenten voor consumenten aan weerszijden',
      ],
      0,
      'Hotelling claimt: concurrenten clusteren in midden. Fout! D\'Aspremont-Gabszewicz-Thisse (1979): uniek evenwicht = uitersten. Minimale differentiatie. Lineaire kosten → discontinu prijsspel → Bertrand-onderbieiding.',
    ],
    [
      'In Tullocks rent-seeking-contestmodel, waarin deelnemers middelen besteden om een vaste prijs te winnen volgens een contestsuccesfunctie, wat is het opmerkelijke resultaat over totale in evenwicht verspilde middelen?',
      [
        'Met een voldoende responsieve contestsuccesfunctie kan de aggregate rent-seeking-uitgave de waarde van de prijs zelf overstijgen, wat de rente overdissipeert',
        'De aggregate uitgave is altijd precies gelijk aan de waarde van de prijs die op het spel staat, wat de rente volledig dissipeert maar nooit overstijgt, ongeacht hoeveel deelnemers meedoen aan het contest',
        'De aggregate uitgave ligt altijd strikt onder de waarde van de prijs, omdat risicoaversie een plafond stelt aan hoeveel elke deelnemer bereid is te besteden',
        'Alleen de uiteindelijke winnaar maakt kosten in evenwicht, omdat verliezende deelnemers zich kosteloos kunnen terugtrekken voordat het contest afloopt',
      ],
      0,
      'Voldoende convexe contestfunctie: totale rent-seeking kan prijswaarde overstijgen (overdissipatie). Niet alleen gelijk aan prijs. Formeel modellering van lobbywerk en patentwedloop. Tullock (1980).',
    ],
    [
      'De theorie van tweezijdige markten van Rochet en Tirole verklaart waarom een platform (zoals een betaalkaartnetwerk) sterk asymmetrische prijzen kan rekenen aan de twee zijden die het verbindt. Wat is het kerninzicht achter die asymmetrie?',
      [
        'Volume hangt af van hoe de prijs verdeeld is tussen de twee zijden, niet alleen van het totale niveau, omdat de aanwezigheid van elke zijde een kruiszijdige externaliteit creëert die de andere zijde niet betaalt',
        'De marginale kost van het bedienen van elke zijde verschilt zo enorm dat asymmetrische prijsstelling eenvoudig die onderliggende kostenverschillen weerspiegelt, zonder enige strategische logica, als een pure doorberekening zonder rol voor externaliteiten',
        'Toezichthouders vereisen wettelijk dat tweezijdige platforms de zijde met het laagste gemiddelde huishoudinkomen subsidiëren',
        'Tweezijdige markten zijn wiskundig identiek aan standaard eenzijdige markten zodra de vraagcurves van de twee zijden simpelweg worden opgeteld',
      ],
      0,
      'Tweezijdig: kruiszijdige externaliteiten. Prijs-structuur doet ertoe (niet alleen niveau). Platform subsidieert gevoeliger zijde/grotere externaliteit. Beperkt mededingingsrecht. Nobelprijs 2014 (Tirole).',
    ],
    [
      'Milgrom en Roberts\'s signaleringsmodel van limietprijsstelling (1982) toont dat een monopolist die een toetreder tegenover zich heeft die onzeker is over de kosten van de monopolist, wat zal doen ten opzichte van de volledige-informatie-optimale prijs van de monopolist?',
      [
        'De prijs onder het volledige-informatieniveau van monopolieprijs zetten, om lage kosten te signaleren en toetreding te ontmoedigen, ook al gaat dit ten koste van huidige winst',
        'De prijs precies op het volledige-informatieniveau van monopolieprijs zetten, omdat limietprijsstelling niets geloofwaardig kan signaleren zodra toetreding als een correct sequentieel spel wordt gemodelleerd',
        'De prijs boven het volledige-informatieniveau zetten, en zichtbaar hoge huidige winst gebruiken als een kostbaar signaal van sterkte dat toetreding afschrikt',
        'De prijs uniform randomiseren over alle haalbare niveaus, omdat elke deterministische prijsregel het type van de monopolist volledig onthult en daarom nutteloos is',
      ],
      0,
      'Oude limietprijstheorie: ongeloofwaardig onder volledige informatie. Milgrom-Roberts: onvolledige informatie herstelt geloofwaardigheid. Laag-kost prijzen onder optimum als kostbaar Bayesiaans signaal tegen toetreding.',
    ],
  ],
};
